import { useRef } from 'react'
import {
  Bold,
  Italic,
  Strikethrough,
  X,
  Image as ImageIcon,
  Music2 as MusicIcon,
  Video as VideoIcon,
  File as FileIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'
import type { TextMessageNodeData } from '@/features/flow/_builder/components/blocks/nodes/text-message-node/text-message.node'
import type { BuilderNodeType } from '@/features/flow/_builder/components/blocks/types'

type TextMessageNodePropertyPanelProps = Readonly<{
  id: string
  type: BuilderNodeType
  data: TextMessageNodeData
  updateData: (data: Partial<TextMessageNodeData>) => void
}>

export default function TextMessageNodePropertyPanel({
  id,
  data,
  updateData,
}: TextMessageNodePropertyPanelProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const audioInputRef = useRef<HTMLInputElement | null>(null)
  const videoInputRef = useRef<HTMLInputElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const VARIABLES = [
    { label: 'Contact Name', token: '{contact.name}' },
    { label: 'Contact Phone', token: '{contact.phone}' },
    { label: 'User Name', token: '{user.name}' },
    { label: 'Ticket ID', token: '{ticket.id}' },
    { label: 'Hora atual', token: '{now}' },
  ] as const

  function applyWrap(marker: string, endMarker?: string) {
    const el = textareaRef.current
    if (!el) return
    const start = el.selectionStart ?? 0
    const end = el.selectionEnd ?? 0
    const value = data.message ?? ''
    const selected = value.slice(start, end)
    const rightMarker = endMarker ?? marker
    const wrapped = `${marker}${selected || 'text'}${rightMarker}`
    const newValue = value.slice(0, start) + wrapped + value.slice(end)
    updateData({ message: newValue })
    requestAnimationFrame(() => {
      el.focus()
      const newCursor = start + wrapped.length
      el.setSelectionRange(newCursor, newCursor)
    })
  }

  function insertVariable(token: string) {
    const el = textareaRef.current
    if (!el) return
    const start = el.selectionStart ?? 0
    const end = el.selectionEnd ?? 0
    const value = data.message ?? ''
    const newValue = value.slice(0, start) + token + value.slice(end)
    updateData({ message: newValue })
    requestAnimationFrame(() => {
      el.focus()
      const newCursor = start + token.length
      el.setSelectionRange(newCursor, newCursor)
    })
  }

  function handleFileToDataUrl(
    fileList: FileList | null | undefined,
    field: 'image' | 'audio' | 'video' | 'file'
  ) {
    const file = fileList?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      updateData({ [field]: reader.result as string })
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className='gap-4.5 flex flex-col p-4'>
      <div className='self-end text-xs text-card-foreground/20'>{id}</div>

      <div className='flex flex-col'>
        <div className='text-xs font-semibold text-card-foreground/60'>
          Mensagem
        </div>
        <div className='mt-2 flex flex-col gap-2'>
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              size='xs'
              variant='outline'
              onClick={() => applyWrap('**')}
              title='Negrito'
            >
              <Bold className='h-4 w-4' />
            </Button>
            <Button
              type='button'
              size='xs'
              variant='outline'
              onClick={() => applyWrap('_')}
              title='Itálico'
            >
              <Italic className='h-4 w-4' />
            </Button>
            <Button
              type='button'
              size='xs'
              variant='outline'
              onClick={() => applyWrap('~~')}
              title='Tachado'
            >
              <Strikethrough className='h-4 w-4' />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type='button' size='xs' variant='outline'>
                  Variáveis
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start'>
                <DropdownMenuLabel>Inserir variável</DropdownMenuLabel>
                {VARIABLES.map((v) => (
                  <DropdownMenuItem
                    key={v.token}
                    onClick={() => insertVariable(v.token)}
                  >
                    {v.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <Textarea
            ref={textareaRef}
            value={data.message}
            onChange={(e) => updateData({ message: e.target.value })}
            placeholder='Digite sua mensagem aqui...'
            className='min-h-32 w-full resize-none'
          />
        </div>
      </div>

      <div className='mt-4 flex flex-col gap-2'>
        <div className='text-xs font-semibold text-card-foreground/60'>
          Anexos
        </div>

        <div className='grid grid-cols-2 gap-4 pb-8'>
          <div className='relative flex flex-col gap-2'>
            <div className='text-xs text-card-foreground/60'>Imagem</div>
            <div className='flex flex-1 items-center gap-3'>
              <button
                type='button'
                onClick={() => imageInputRef.current?.click()}
                className='flex size-16 w-full items-center justify-center overflow-hidden rounded-md border'
              >
                {data.image ? (
                  <img
                    src={data.image}
                    className='h-full w-full object-cover'
                  />
                ) : (
                  <ImageIcon className='h-5 w-5 text-muted-foreground' />
                )}
              </button>
              {data.image && (
                <Button
                  className='absolute -bottom-8 w-full'
                  type='button'
                  size='xs'
                  variant='outline'
                  onClick={() => updateData({ image: null })}
                >
                  <X className='h-4 w-4' />
                  Remover
                </Button>
              )}
            </div>
            <input
              ref={imageInputRef}
              type='file'
              accept='image/*'
              className='hidden'
              onChange={(e) => handleFileToDataUrl(e.target.files, 'image')}
            />
          </div>

          {/* Áudio */}
          <div className='relative flex flex-col gap-2'>
            <div className='text-xs text-card-foreground/60'>Áudio</div>
            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={() => audioInputRef.current?.click()}
                className='flex size-16 w-full items-center justify-center rounded-md border'
              >
                {data.audio ? (
                  <p>Audio preview</p>
                ) : (
                  <MusicIcon className='h-5 w-5 text-muted-foreground' />
                )}
              </button>
              {data.audio && (
                <Button
                  className='absolute -bottom-8 w-full'
                  type='button'
                  size='xs'
                  variant='outline'
                  onClick={() => updateData({ audio: null })}
                >
                  <X className='h-4 w-4' />
                  Remover
                </Button>
              )}
            </div>
            <input
              ref={audioInputRef}
              type='file'
              accept='audio/*'
              className='hidden'
              onChange={(e) => handleFileToDataUrl(e.target.files, 'audio')}
            />
          </div>

          {/* Vídeo */}
          <div className='relative flex flex-col gap-2'>
            <div className='text-xs text-card-foreground/60'>Vídeo</div>
            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={() => videoInputRef.current?.click()}
                className='flex size-16 w-full items-center justify-center overflow-hidden rounded-md border'
              >
                {data.video ? (
                  <video
                    src={data.video}
                    className='h-full w-full object-cover'
                    controls
                  />
                ) : (
                  <VideoIcon className='h-5 w-5 text-muted-foreground' />
                )}
              </button>
              {data.video && (
                <Button
                  className='absolute -bottom-8 w-full'
                  type='button'
                  size='xs'
                  variant='outline'
                  onClick={() => updateData({ video: null })}
                >
                  <X className='h-4 w-4' />
                  Remover
                </Button>
              )}
            </div>
            <input
              ref={videoInputRef}
              type='file'
              accept='video/*'
              className='hidden'
              onChange={(e) => handleFileToDataUrl(e.target.files, 'video')}
            />
          </div>

          {/* Arquivo */}
          <div className='relative flex flex-col gap-2'>
            <div className='text-xs text-card-foreground/60'>Arquivo</div>
            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                className='flex size-16 w-full items-center justify-center rounded-md border'
              >
                {data.file ? (
                  <p>Arquivo anexado</p>
                ) : (
                  <FileIcon className='h-5 w-5 text-muted-foreground' />
                )}
              </button>
              {data.file && (
                <Button
                  className='absolute -bottom-8 w-full'
                  type='button'
                  size='xs'
                  variant='outline'
                  onClick={() => updateData({ file: null })}
                >
                  <X className='h-4 w-4' />
                  Remover
                </Button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type='file'
              className='hidden'
              onChange={(e) => handleFileToDataUrl(e.target.files, 'file')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
